import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import logger from '@/util/logger';
import { Check, Copy } from 'lucide-react';
import type { FC } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

const log = logger('IdWithCopy');

type IdWithCopyProps = {
    id: string;
    className?: string;
    variant?: 'default' | 'muted' | 'mono';
    showLabel?: boolean;
    to?: string;
};

const IdWithCopy: FC<IdWithCopyProps> = ({ id, className = '', variant = 'default', showLabel = false, to }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(id);
            setCopied(true);
            setTimeout(() => {
                setCopied(false);
            }, 3000);
        } catch (err) {
            log.warn('Could not copy an id to the clipboard', { err, id });
        }
    };

    const textClasses = {
        default: 'text-muted-foreground',
        muted: 'text-muted-foreground',
        mono: 'font-mono text-xs text-muted-foreground',
    }[variant];
    const text = `${showLabel ? 'ID: ' : ''}${id}`;

    return (
        <div className={`flex items-center gap-2 ${className}`}>
            {to ? (
                <Link to={to} className={cn('break-all', textClasses, 'text-sky-500 hover:underline')}>
                    {text}
                </Link>
            ) : (
                <span className={`break-all ${textClasses}`}>{text}</span>
            )}
            <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 flex-shrink-0"
                onClick={handleCopy}
                aria-label="Copy ID to clipboard"
            >
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
            </Button>
        </div>
    );
};

export default IdWithCopy;
